import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { BookOpen, Plus, Receipt, TrendingDown, TrendingUp, Image as ImageIcon } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

function IDR(n) { return new Intl.NumberFormat("id-ID", { style:"currency", currency:"IDR", maximumFractionDigits:0 }).format(n || 0); }
async function fileToBase64(f) { return new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result);r.onerror=rej;r.readAsDataURL(f);}); }

export default function BukuKas() {
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeProgram, setActiveProgram] = useState("ALL");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ program_id: "", kind: "EXPENSE", date: new Date().toISOString().slice(0,10), amount: "", description: "", notes: "", receipt_base64: "", receipt_name: "" });
  const [viewReceipt, setViewReceipt] = useState(null);

  const canWrite = ["SUPER_ADMIN","ADMIN_KLASIS","BENDAHARA_KLASIS","SEKRETARIS_KLASIS","KETUA_KLASIS","KOORDINATOR_BIDANG"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    const [p, t] = await Promise.all([api.get("/programs"), api.get("/program-transactions")]);
    setPrograms(p.data); setTxns(t.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => txns.filter(t => activeProgram === "ALL" || t.program_id === activeProgram), [txns, activeProgram]);
  const totalExpense = filtered.filter(t => t.kind === "EXPENSE").reduce((s,t)=>s+t.amount, 0);
  const totalIncome = filtered.filter(t => t.kind === "INCOME").reduce((s,t)=>s+t.amount, 0);
  const balance = totalIncome - totalExpense;
  const activeProg = programs.find(p => p.id === activeProgram);

  const submit = async () => {
    if (!form.program_id || !form.amount || !form.description) { toast.error("Program, jumlah, dan deskripsi wajib."); return; }
    try {
      await api.post("/program-transactions", {
        ...form,
        amount: Number(form.amount),
        receipt_base64: form.receipt_base64 || null,
        receipt_name: form.receipt_name || null,
        notes: form.notes || null,
      });
      toast.success("Transaksi dicatat & realisasi program ter-update otomatis.");
      setOpen(false);
      setForm({ program_id: form.program_id, kind: "EXPENSE", date: new Date().toISOString().slice(0,10), amount: "", description: "", notes: "", receipt_base64: "", receipt_name: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const showReceipt = async (id) => {
    try {
      const { data } = await api.get(`/program-transactions/${id}/receipt`);
      if (!data.receipt_base64) { toast.info("Belum ada bukti diunggah."); return; }
      setViewReceipt(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Keuangan</div>
          <h1 className="font-heading font-bold text-3xl mt-1">Buku Kas Program</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Jurnal transaksi per program. Setiap pengeluaran otomatis memperbarui realisasi anggaran &amp; progress program.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={activeProgram} onValueChange={setActiveProgram}>
            <SelectTrigger className="w-72" data-testid="select-buku-program"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua Program</SelectItem>
              {programs.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {canWrite && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full" onClick={()=>setForm({...form, program_id: activeProgram !== "ALL" ? activeProgram : ""})} data-testid="btn-new-txn"><Plus className="h-4 w-4 mr-2" /> Transaksi</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Catat Transaksi Program</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Program</Label>
                    <Select value={form.program_id} onValueChange={(v)=>setForm({...form, program_id:v})}>
                      <SelectTrigger data-testid="select-txn-program"><SelectValue placeholder="Pilih program" /></SelectTrigger>
                      <SelectContent>{programs.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Jenis</Label>
                      <Select value={form.kind} onValueChange={(v)=>setForm({...form, kind:v})}>
                        <SelectTrigger data-testid="select-txn-kind"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="EXPENSE">Pengeluaran</SelectItem>
                          <SelectItem value="INCOME">Penerimaan</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})} data-testid="input-txn-date" /></div>
                  </div>
                  <div><Label>Jumlah (Rp)</Label><Input type="number" value={form.amount} onChange={(e)=>setForm({...form, amount:e.target.value})} data-testid="input-txn-amount" /></div>
                  <div><Label>Deskripsi / Keterangan</Label><Input value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} data-testid="input-txn-desc" /></div>
                  <div>
                    <Label>Bukti (JPG/PNG maks 1.5MB, opsional)</Label>
                    <Input type="file" accept="image/*" data-testid="input-txn-receipt"
                      onChange={async (e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        if (f.size > 1_500_000) { toast.error("File terlalu besar"); return; }
                        setForm({...form, receipt_base64: await fileToBase64(f), receipt_name: f.name});
                      }}
                    />
                    {form.receipt_name && <div className="text-xs text-muted-foreground mt-1">✓ {form.receipt_name}</div>}
                  </div>
                  <div><Label>Catatan</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} /></div>
                </div>
                <DialogFooter><Button onClick={submit} data-testid="btn-save-txn">Simpan Transaksi</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card><CardContent className="p-5">
          <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Pengeluaran</div>
          <div className="font-heading font-bold text-2xl mt-1 text-rose-700">{IDR(totalExpense)}</div>
          <TrendingDown className="h-4 w-4 text-rose-500 mt-1" />
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Penerimaan</div>
          <div className="font-heading font-bold text-2xl mt-1 text-emerald-700">{IDR(totalIncome)}</div>
          <TrendingUp className="h-4 w-4 text-emerald-500 mt-1" />
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Saldo</div>
          <div className={`font-heading font-bold text-2xl mt-1 ${balance >= 0 ? "text-primary" : "text-destructive"}`}>{IDR(balance)}</div>
        </CardContent></Card>
        {activeProg && activeProg.budget != null && (
          <Card><CardContent className="p-5">
            <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">Realisasi vs Anggaran</div>
            <div className="font-heading font-bold text-lg mt-1 text-accent">{IDR(activeProg.realisasi || 0)} / {IDR(activeProg.budget)}</div>
            <Progress value={activeProg.budget ? Math.min(100, (activeProg.realisasi || 0) * 100 / activeProg.budget) : 0} className="h-1.5 mt-2" />
          </CardContent></Card>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Jurnal Transaksi</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? <div className="p-6 text-sm text-muted-foreground">Memuat…</div> : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <BookOpen className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
              <div className="text-sm text-muted-foreground">Belum ada transaksi. Klik "Transaksi" untuk mencatat.</div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tanggal</TableHead><TableHead>Program</TableHead>
                  <TableHead>Deskripsi</TableHead><TableHead>Jenis</TableHead>
                  <TableHead className="text-right">Jumlah</TableHead><TableHead>Bukti</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((t) => {
                  const p = programs.find(x => x.id === t.program_id);
                  return (
                    <TableRow key={t.id} data-testid={`txn-${t.id}`}>
                      <TableCell className="text-xs">{t.date}</TableCell>
                      <TableCell className="text-xs">{p?.name || "—"}</TableCell>
                      <TableCell>{t.description}</TableCell>
                      <TableCell><StatusBadge status={t.kind === "EXPENSE" ? "destructive" : "success"} label={t.kind === "EXPENSE" ? "Keluar" : "Masuk"} /></TableCell>
                      <TableCell className={`text-right font-mono font-semibold ${t.kind === "EXPENSE" ? "text-rose-700" : "text-emerald-700"}`}>{IDR(t.amount)}</TableCell>
                      <TableCell>
                        {t.receipt_name ? (
                          <button onClick={()=>showReceipt(t.id)} className="text-primary hover:underline text-xs flex items-center gap-1" data-testid={`btn-view-receipt-${t.id}`}>
                            <ImageIcon className="h-3 w-3" /> lihat
                          </button>
                        ) : <span className="text-xs text-muted-foreground italic">—</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!viewReceipt} onOpenChange={(v)=>!v && setViewReceipt(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Bukti Transaksi</DialogTitle></DialogHeader>
          {viewReceipt?.receipt_base64 && <img src={viewReceipt.receipt_base64} alt="bukti" className="w-full rounded-md border border-border" />}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
